import type React from "react";
import { useTranslation } from "../hooks/useTranslation";
import { useAppStore } from "../store/appStore";
import appStyles from "../styles/styles";
import LanguageSwitcher from "./LanguageSwitcher";

interface SettingsProps {
	isRecording: boolean;
	microphones: Array<{ deviceId: string; label: string }>;
	selectedMicrophoneId: string | undefined;
	selectMicrophone: (deviceId: string) => void;
	selectedRecognitionLanguage: string;
	setSelectedRecognitionLanguage: (language: string) => void;
	availableLanguages: Array<{ code: string; name: string }>;
}

export const Settings: React.FC<SettingsProps> = ({
	isRecording,
	microphones,
	selectedMicrophoneId,
	selectMicrophone,
	selectedRecognitionLanguage,
	setSelectedRecognitionLanguage,
	availableLanguages,
}) => {
	const isSpeechRecognitionAvailable = useAppStore(state => state.isSpeechRecognitionAvailable);

	return (
		<>
			<MicrophoneSelector
				isRecording={isRecording}
				microphones={microphones}
				selectedMicrophoneId={selectedMicrophoneId}
				selectMicrophone={selectMicrophone}
			/>
			<RecognitionLanguageSelector
				isRecording={isRecording}
				selectedLanguage={selectedRecognitionLanguage}
				setSelectedLanguage={setSelectedRecognitionLanguage}
				availableLanguages={availableLanguages}
				disabled={!isSpeechRecognitionAvailable}
			/>
			<UILanguageSelector />
			<NightModeToggle />
		</>
	);
};

interface MicrophoneSelectorProps {
	isRecording: boolean;
	microphones: Array<{ deviceId: string; label: string }>;
	selectedMicrophoneId: string | undefined;
	selectMicrophone: (deviceId: string) => void;
}

const MicrophoneSelector: React.FC<MicrophoneSelectorProps> = ({
	isRecording,
	microphones,
	selectedMicrophoneId,
	selectMicrophone,
}) => {
	const t = useTranslation();

	return (
		<div className={appStyles.formGroup}>
			<label htmlFor="microphone-select" className={appStyles.label}>
				{t.microphoneSelection}
				{isRecording && <span className="ml-2 text-red-500 text-xs">{t.cantChangeWhileRecording}</span>}
			</label>
			<select
				id="microphone-select"
				className={appStyles.select}
				value={selectedMicrophoneId || ""}
				onChange={(e) => selectMicrophone(e.target.value)}
				disabled={isRecording}
			>
				<option value="" disabled>
					{t.selectMicrophone}
				</option>
				{microphones.map((mic) => (
					<option key={mic.deviceId} value={mic.deviceId}>
						{mic.label || `${t.micLabel} ${mic.deviceId.slice(0, 5)}...`}
					</option>
				))}
			</select>
		</div>
	);
};

interface RecognitionLanguageSelectorProps {
	isRecording: boolean;
	selectedLanguage: string;
	setSelectedLanguage: (language: string) => void;
	availableLanguages: Array<{ code: string; name: string }>;
	disabled?: boolean;
}

const RecognitionLanguageSelector: React.FC<RecognitionLanguageSelectorProps> = ({
	isRecording,
	selectedLanguage,
	setSelectedLanguage,
	availableLanguages,
	disabled = false,
}) => {
	const t = useTranslation();

	return (
		<div className={appStyles.formGroup}>
			<label htmlFor="recognition-language-select" className={appStyles.label}>
				{t.recognitionLanguage}
				{isRecording && <span className="ml-2 text-red-500 text-xs">{t.cantChangeWhileRecording}</span>}
			</label>
			<select
				id="recognition-language-select"
				className={appStyles.select}
				value={selectedLanguage}
				onChange={(e) => setSelectedLanguage(e.target.value)}
				disabled={isRecording || disabled}
			>
				{availableLanguages.map((lang: { code: string; name: string }) => (
					<option key={lang.code} value={lang.code}>
						{lang.name}
					</option>
				))}
			</select>
			{disabled && !isRecording && (
				<div className="mt-2 text-amber-600 dark:text-amber-400 text-sm italic">
					{t.speechRecognitionNotSupportedInSelector}
				</div>
			)}
		</div>
	);
};

const UILanguageSelector: React.FC = () => {
	const t = useTranslation();

	return (
		<div className="flex flex-col mt-4">
			<div id="ui-language-label" className="mb-2 font-medium text-sm text-gray-700 dark:text-gray-300">
				{t.uiLanguage}
			</div>
			<div className="mt-1" aria-labelledby="ui-language-label">
				<LanguageSwitcher />
			</div>
		</div>
	);
};

const NightModeToggle: React.FC = () => {
	const darkMode = useAppStore((state) => state.darkMode);
	const setDarkMode = useAppStore((state) => state.setDarkMode);
	const t = useTranslation();

	return (
		<div className="flex items-center mt-4">
			<label className="flex items-center cursor-pointer">
				<input
					type="checkbox"
					checked={darkMode}
					onChange={() => setDarkMode(!darkMode)}
					className="form-checkbox h-5 w-5 text-blue-600"
				/>
				<span className="ml-2 flex items-center text-gray-700 dark:text-white">
					{darkMode ? <span className="mr-1">🌙</span> : <span className="mr-1">☀️</span>}
					{t.nightMode}
				</span>
			</label>
		</div>
	);
};

export default Settings;
